import React, { useEffect, useState, useCallback } from 'react';
import { usePlaidLink, PlaidLinkError } from 'react-plaid-link';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Ban as Bank, CreditCard, Landmark, Wallet, TrendingUp } from 'lucide-react';
import { fetchAccountPerformance } from '@/services/api/accountsApi';
import { AccountPerformance } from '@/types/AccountPerformance';

const getAccountIcon = (type: string) => {
  switch (type) {
    case 'credit': return <CreditCard className="h-5 w-5 text-purple-500" />;
    case 'depository': return <Wallet className="h-5 w-5 text-blue-500" />;
    case 'savings': return <Landmark className="h-5 w-5 text-green-500" />;
    case 'investment': return <TrendingUp className="h-5 w-5 text-yellow-500" />;
    default: return <Bank className="h-5 w-5 text-gray-500" />;
  }
};

const Accounts: React.FC = () => {
  const [performance, setPerformance] = useState<AccountPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLinkLoading, setIsLinkLoading] = useState(false);

  // 🔁 Load account performance (used on first load + after linking)
  const loadAccountPerformance = async () => {
    try {
      setPerformanceLoading(true);
      const data = await fetchAccountPerformance();
      setPerformance(data);
    } catch (err) {
      console.error("❌ Failed to fetch performance data", err);
      setError("Unable to load performance data");
    } finally {
      setPerformanceLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccountPerformance();
  }, []);

  // 🔗 Fetch Plaid link token
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        const res = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/plaid/link-token`, {
          withCredentials: true,
        });
        setLinkToken(res.data?.link_token ?? null);
        console.log('✅ Plaid link token:', res.data?.link_token);
      } catch (err) {
        console.error('❌ Failed to fetch Plaid link token:', err);
      }
    };

    fetchLinkToken();
  }, []);

  // 🧩 Plaid Link config
  const { open, ready } = usePlaidLink({
    token: linkToken || '',
    onSuccess: useCallback(async (public_token: string) => {
      try {
        setIsLinkLoading(true);
        await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/plaid/exchange-token`, { public_token }, {
          withCredentials: true,
        });
        alert('✅ Bank account linked successfully!');
        await loadAccountPerformance(); // 🔁 Refresh after linking
      } catch (err) {
        console.error('❌ Error exchanging public token:', err);
      } finally {
        setIsLinkLoading(false);
      }
    }, []),
    onExit: useCallback((err: PlaidLinkError | null) => {
      console.warn('👋 User exited Plaid Link', err);
      setIsLinkLoading(false);
    }, []),
  });

  const handlePlaidLink = () => {
    if (!linkToken) {
      console.warn("❌ Missing link token");
      return;
    }
    if (!ready) {
      console.warn("⚠️ Plaid is not ready");
      return;
    }
    setIsLinkLoading(true);
    open();
  };

  if (loading || performanceLoading) return <div className="p-6">Refreshing account data...</div>;
  if (error || !performance) return <div className="p-6 text-red-600">{error || 'No data found'}</div>;

  const totalAccounts = performance.accountGroups?.reduce((acc, group) => acc + group.accounts.length, 0) || 0;
  const creditAccounts = performance.accountGroups?.flatMap(g => g.accounts)?.filter(a => a.type === 'credit').length || 0;
  const bankAccounts = totalAccounts - creditAccounts;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Accounts</h1>
        <Button
          leftIcon={<Bank size={16} />}
          onClick={handlePlaidLink}
          disabled={!ready || isLinkLoading}
        >
          {isLinkLoading ? 'Connecting...' : 'Link Bank Account'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card><CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Assets</p>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">${(performance.totalAssets ?? 0).toFixed(2)}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Across all assets</p>
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Credit Card Debt</p>
          <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">${(performance.totalLiabilities ?? 0).toFixed(2)}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Outstanding balance</p>
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Net Worth</p>
          <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">${(performance.netWorth ?? 0).toFixed(2)}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {performance.netChangePercent > 0 ? '+' : ''}${(performance.netChangePercent ?? 0).toFixed(2)}% this month
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Accounts</p>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{totalAccounts}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{creditAccounts} Credit, {bankAccounts} Bank</p>
        </CardContent></Card>
      </div>

      {/* Account Table */}
      <div className="bg-white dark:bg-gray-900 shadow-sm border rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Institution</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Change %</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900">
            {performance.accountGroups.flatMap(group =>
              group.accounts.map(account => (
                <tr key={account.accountId}>
                  <td className="px-6 py-4 whitespace-nowrap flex items-center gap-2">
                    {getAccountIcon(account.subtype)}
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{account.name}</div>
                      <div className="text-xs text-gray-400">•••• {account.accountId.toString().slice(-4)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 capitalize">{account.subtype}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">Plaid</td>
                  <td className={`px-6 py-4 font-medium ${account.currentBalance < 0 ? 'text-red-600' : 'text-green-700'}`}>
                    ${(account.currentBalance ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {account.changePercent > 0 ? '+' : ''}{(account.changePercent ?? 0).toFixed(2)}%
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex px-2 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                      {account.currentBalance >= 0 ? 'Active' : 'Good Standing'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <Link to={`/accounts/${account.accountId}/details`} className="text-emerald-600 hover:text-emerald-900">→</Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Accounts;
